import { api } from './api';

/** Этап 1.3: каталог на данных сервера — себестоимость, цена B2C, маржа; карточка товара с разбором цены и партиями. */
export type CatalogRow = {
  sku: string;
  supplierArticle: string | null;
  name: string;
  category: string;
  source: 'Own' | 'Partner';
  partnerId: string | null;
  partnerName: string | null;
  color: string | null;
  base: string | null;
  stock: number;
  incomingQty: number;
  incomingEta: string | null;
  cost: number | null;
  sale: number | null;
  marginAed: number | null;
  marginPct: number | null;
  image: string | null;
  published: boolean;
  listingStatus: string;
  weightKg: number | null;
  cbm: number | null;
  specialOffer: boolean;
  status: string;
};

export type CatalogMeta = {
  total: number;
  partnerCount: number;
  ownCount: number;
  categories: { code: string; count: number }[];
  partners: { id: string; shortName: string }[];
  colors: string[];
  bases: string[];
  maxPrice: number;
  activePartners: number;
};

export type CatalogQuery = {
  source?: 'All' | 'Own' | 'Partner';
  partnerId?: string;
  category?: string;
  inStock?: boolean;
  maxPrice?: number;
  color?: string;
  base?: string;
  query?: string;
  sort?: string;
  limit?: number;
};

export type LotBasis = { lotId: string | null; kind: string; qty: number; unitPrice: number; lotCost: number };

export type ProductCard = {
  sku: string;
  supplierArticle: string | null;
  name: string;
  category: string;
  source: 'Own' | 'Partner';
  status: string;
  color: string | null;
  base: string | null;
  description: string | null;
  specs: { label: string; value: string }[];
  fabrics: { name: string; tone: string }[];
  options: { label: string; surcharge: number }[];
  packingVariants: unknown[];
  packing: string | null;
  cbm: number | null;
  weightKg: number | null;
  dims: string | null;
  unitsPerCarton: number | null;
  leadTime: string | null;
  origin: string | null;
  incoterm: string | null;
  hsCode: string | null;
  minOrder: number | null;
  warrantyMonths: number | null;
  specialOffer: boolean;
  image: string | null;
  images: string[];
  stock: number;
  incomingQty: number;
  incomingEta: string | null;
  lastStockAt: string | null;
  lots: { id: string; kind: string; qty: number; unitPrice: number; packing: string | null; eta: string | null; etaText: string | null; receivedAt: string | null }[];
  partner: {
    id: string;
    name: string;
    shortName: string | null;
    city: string | null;
    warehouse: string | null;
    contractStatus: string;
    status: string;
    discountPct: number;
    noDiscountOnSpecial: boolean;
    reservationPeriodDays: number;
    minResaleType: string;
    minResaleValue: number | null;
    lastPriceList: string | null;
  } | null;
  pricing: {
    partnerPrice: number | null;
    oldStockPrice: number | null;
    discountPct: number;
    cost: number | null;
    markup: number;
    markupSource: 'product' | 'partner_category' | 'partner_rule' | 'category' | 'partner' | 'default';
    markupOverride: number | null;
    rounding: string;
    minResale: number | null;
    calcSale: number | null;
    priceBasis: string | null;
    basis: LotBasis[];
    sale: number | null;
    saleManual: boolean;
    b2b: number | null;
    rrp: number | null;
    marginAed: number | null;
    marginPct: number | null;
  };
  listing: { status: string; listedOnSite: boolean; siteUrl: string | null; previewUrl: string | null };
  usedIn: { id: string; code: string; project: string | null; client: string | null; status: string; qty: number }[];
  siteSales?: { unitsTotal: number; ordersTotal: number; units30d: number; orders30d: number; revenue30d: number; lastSoldAt: string | null } | null;
};

const qs = (q: CatalogQuery) => {
  const p = new URLSearchParams();
  if (q.source && q.source !== 'All') p.set('source', q.source);
  if (q.partnerId && q.partnerId !== 'All') p.set('partnerId', q.partnerId);
  if (q.category && q.category !== 'All') p.set('category', q.category);
  if (q.inStock) p.set('inStock', 'true');
  if (q.maxPrice != null) p.set('maxPrice', String(q.maxPrice));
  if (q.color && q.color !== 'All') p.set('color', q.color);
  if (q.base && q.base !== 'All') p.set('base', q.base);
  if (q.query) p.set('query', q.query);
  if (q.sort) p.set('sort', q.sort);
  if (q.limit) p.set('limit', String(q.limit));
  const s = p.toString();
  return s ? `?${s}` : '';
};

export const catalogApi = {
  list: (q: CatalogQuery = {}) => api.get<CatalogRow[]>(`/catalog${qs(q)}`),
  meta: () => api.get<CatalogMeta>('/catalog/meta'),
  product: (sku: string) => api.get<ProductCard>(`/catalog/${encodeURIComponent(sku)}`),
  patch: (sku: string, body: { markupOverride?: number | null; minResalePrice?: number | null }) => api.patch<ProductCard>(`/catalog/${encodeURIComponent(sku)}`, body),
  addToSpec: (sku: string, body: { specificationId: string; qty: number; option?: string | null }) => api.post<{ specificationId: string; code: string; lineId: string; qty: number }>(`/catalog/${encodeURIComponent(sku)}/add-to-spec`, body),
};
